import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Plus, Search, Trash2, Copy, ArrowUp, ArrowDown, GripVertical, Columns3, X, Filter, ChevronsUpDown, CalendarDays, Download, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useStatuses } from "@/context/StatusContext";
import { useLiveRefresh } from "@/hooks/useLiveRefresh";
import PageHeader from "@/components/PageHeader";
import { StatoBadge, PrioritaBadge } from "@/components/Badges";
import { STATUS_LABEL, PRIORITY, CAT_COLOR, PICCO_OPTIONS } from "@/lib/constants";
import { addMinutes, fmtDate } from "@/lib/time";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { DateField, TimeField } from "@/components/DateTimeField";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ProductCombobox } from "@/components/ProductCombobox";

const empty = {
  client_name: "", committente_id: "", category: "Installazione", priority: "media",
  assigned_to: "", site: "", address: "", comune: "", description: "", client_phone: "",
  id_olo: "", id_risorsa: "", pfs: "", device_password: "", id_servizi: "", picco: "", scheduled_date: "", appointment_time: "", intervention_date: "", start_time: "", duration_min: 120,
  planned_materials: [],
};

function QuickEnumFilter({ label, colId, options, selected, setFilters }) {
  const sel = selected || [];
  const toggle = (v) => setFilters((f) => { const cur = f[colId] || []; return { ...f, [colId]: cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v] }; });
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" data-testid={`quick-filter-${colId}`}>
          <Filter size={14} />{label}
          {sel.length > 0 && <span className="ml-1 rounded-full bg-primary/10 px-1.5 text-xs font-bold text-primary">{sel.length}</span>}
          {sel.length > 0 && <span onClick={(e) => { e.stopPropagation(); setFilters((f) => ({ ...f, [colId]: [] })); }} data-testid={`quick-filter-${colId}-clear`} className="ml-1 rounded-full p-0.5 hover:bg-secondary"><X size={13} /></span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-2">
        <div className="mb-2 flex items-center justify-between gap-2 border-b border-border pb-2">
          <button onClick={() => setFilters((f) => ({ ...f, [colId]: options.map((o) => o.v) }))} className="text-xs font-semibold text-primary hover:underline" data-testid={`quick-select-all-${colId}`}>Seleziona tutto</button>
          <button onClick={() => setFilters((f) => ({ ...f, [colId]: [] }))} className="text-xs font-semibold text-muted-foreground hover:text-navy" data-testid={`quick-deselect-all-${colId}`}>Deseleziona tutto</button>
        </div>
        <div className="max-h-64 space-y-1 overflow-y-auto">
          {options.map((o) => (
            <label key={o.v} className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm hover:bg-secondary">
              <Checkbox checked={sel.includes(o.v)} onCheckedChange={() => toggle(o.v)} data-testid={`quick-${colId}-${o.v}`} />
              {o.l}
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

const ALL_COLS = [
  { id: "code", label: "Codice", kind: "code", w: 120 },
  { id: "id_olo", label: "ID OLO", kind: "text", w: 140 },
  { id: "id_risorsa", label: "ID Risorsa", kind: "text", w: 140 },
  { id: "pfs", label: "PFS", kind: "text", w: 120 },
  { id: "picco", label: "Picco", kind: "enum", w: 100 },
  { id: "committente", label: "Committente", kind: "enum", w: 140 },
  { id: "client_name", label: "Cliente", kind: "text", w: 160 },
  { id: "comune", label: "Comune", kind: "text", w: 140 },
  { id: "address", label: "Indirizzo", kind: "text", w: 170 },
  { id: "category", label: "Categoria", kind: "cat", w: 140 },
  { id: "assigned_to_name", label: "Tecnico", kind: "enum", w: 190 },
  { id: "appuntamento", label: "Appuntamento", kind: "date", w: 150 },
  { id: "intervento", label: "Intervento", kind: "interval", w: 130 },
  { id: "status", label: "Stato", kind: "status", w: 130 },
  { id: "priority", label: "Priorità", kind: "priority", w: 120 },
  { id: "created_at", label: "Creato", kind: "created", w: 120 },
  { id: "aging_days", label: "Aging", kind: "aging", w: 90 },
  { id: "amministrazione", label: "Amministrazione", kind: "admin", w: 150 },
];
const STORE = "flux_ticket_cols_v1";
const ENUM_COLS = ["committente", "assigned_to_name", "category", "status", "priority", "picco", "amministrazione"];
const TEXT_FILTER_COLS = ["id_olo", "id_risorsa", "pfs", "client_name", "comune", "address"];
const SERVER_FILTERABLE = [...ENUM_COLS, ...TEXT_FILTER_COLS, "appuntamento", "intervento"];
const DATE_FILTER_COLS = ["appuntamento", "intervento"];
const ACTIONS_W = 76;
const PERSIST_BASE = "flux_tickets_view_v1";

const appuntamento = (t) => (t.scheduled_date ? `${fmtDate(t.scheduled_date)} ${t.appointment_time || ""}`.trim() : "");
const intervento = (t) => {
  const range = t.start_time ? `${t.start_time}–${addMinutes(t.start_time, t.duration_min || 0)}` : "";
  const d = t.intervention_date ? fmtDate(t.intervention_date) : "";
  return [d, range].filter(Boolean).join(" ");
};
const pad2 = (n) => String(n).padStart(2, "0");
const toISO = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
function textVal(t, id) {
  if (id === "appuntamento") return appuntamento(t);
  if (id === "intervento") return intervento(t);
  if (id === "created_at") return fmtDate(t.created_at);
  if (id === "status") return t.status;
  if (id === "priority") return t.priority;
  return (t[id] ?? "").toString();
}

export default function Tickets({ area = "ticket" }) {
  const isMnt = area === "manutenzione";
  const PERSIST = isMnt ? `${PERSIST_BASE}_mnt` : PERSIST_BASE;
  const readPersisted = () => { try { return JSON.parse(sessionStorage.getItem(PERSIST)) || {}; } catch { return {}; } };
  const { user } = useAuth();
  const { options: statusOptions } = useStatuses();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [tecnici, setTecnici] = useState([]);
  const [committenti, setCommittenti] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState(() => readPersisted().q || "");
  const [qDebounced, setQDebounced] = useState(() => readPersisted().q || "");
  const [dateFilters, setDateFilters] = useState(() => (readPersisted().dates || []).map((s) => new Date(`${s}T00:00:00`)));
  const [filters, setFilters] = useState(() => readPersisted().filters || {});
  const [sort, setSort] = useState(() => readPersisted().sort || { col: "created_at", dir: "desc" });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [dragCol, setDragCol] = useState(null);
  const [products, setProducts] = useState([]);
  const [selProd, setSelProd] = useState("");
  const [dupOpen, setDupOpen] = useState(false);
  const [dupList, setDupList] = useState([]);
  const [exporting, setExporting] = useState(false);
  const canManage = ["admin", "responsabile"].includes(user?.role);
  const parentRef = useRef(null);
  const pageState = useRef({ page: 1, hasMore: false, loading: false });
  const lastKeyRef = useRef(null);
  const reqSeq = useRef(0);

  const [colState, setColState] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem(STORE));
      if (s?.order && s?.hidden) {
        const missing = ALL_COLS.map((c) => c.id).filter((id) => !s.order.includes(id));
        return { ...s, order: [...s.order, ...missing], widths: s.widths || {} };
      }
    } catch {}
    return { order: ALL_COLS.map((c) => c.id), hidden: [], widths: {} };
  });
  useEffect(() => { localStorage.setItem(STORE, JSON.stringify(colState)); }, [colState]);

  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    sessionStorage.setItem(PERSIST, JSON.stringify({ q, filters, sort, dates: dateFilters.map(toISO) }));
  }, [q, filters, sort, dateFilters]);

  useEffect(() => {
    api.get("/tecnici").then((r) => setTecnici(r.data)).catch(() => {});
    api.get("/committenti").then((r) => setCommittenti(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/magazzino/products").then((r) => setProducts(r.data)).catch(() => setProducts([]));
  }, []);

  const activeFilters = useCallback(() => {
    const af = {};
    Object.entries(filters).forEach(([k, v]) => { if (Array.isArray(v) ? v.length : v) af[k] = v; });
    return af;
  }, [filters]);

  const buildParams = useCallback((pageNum) => {
    const p = { page: pageNum, page_size: 100, sort_col: sort.col, sort_dir: sort.dir };
    if (canManage) p.area = area;
    if (qDebounced) p.q = qDebounced;
    const af = activeFilters();
    if (Object.keys(af).length) p.filters = JSON.stringify(af);
    if (dateFilters.length) p.dates = JSON.stringify(dateFilters.map(toISO));
    return p;
  }, [qDebounced, activeFilters, dateFilters, sort, area, canManage]);

  const fetchFirst = useCallback(async (force = false) => {
    const params = buildParams(1);
    const key = JSON.stringify(params);
    if (!force && key === lastKeyRef.current) return;
    lastKeyRef.current = key;
    const my = ++reqSeq.current;
    pageState.current.loading = true; setLoading(true);
    try {
      const { data } = await api.get("/tickets", { params });
      if (my !== reqSeq.current) return;
      setItems(data.items); setTotal(data.total);
      pageState.current = { page: 1, hasMore: data.has_more, loading: false };
      if (parentRef.current) parentRef.current.scrollTop = 0;
    } catch { if (my === reqSeq.current) { setItems([]); setTotal(0); pageState.current = { page: 1, hasMore: false, loading: false }; } }
    finally { if (my === reqSeq.current) setLoading(false); }
  }, [buildParams]);

  useEffect(() => { fetchFirst(); }, [fetchFirst]);
  useLiveRefresh(() => fetchFirst(true));

  const loadMore = useCallback(async () => {
    const st = pageState.current;
    if (!st.hasMore || st.loading) return;
    st.loading = true; setLoading(true);
    const my = ++reqSeq.current;
    try {
      const next = st.page + 1;
      const { data } = await api.get("/tickets", { params: buildParams(next) });
      if (my !== reqSeq.current) return;
      setItems((prev) => [...(prev || []), ...data.items]); if (data.total != null) setTotal(data.total);
      pageState.current = { page: next, hasMore: data.has_more, loading: false };
    } catch { if (my === reqSeq.current) pageState.current.loading = false; }
    finally { if (my === reqSeq.current) setLoading(false); }
  }, [buildParams]);

  const onScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 500) loadMore();
  };

  const rowVirtualizer = useVirtualizer({
    count: items?.length || 0,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 41,
    overscan: 14,
  });

  const productOptions = useMemo(() => (products || []).map((p) => ({
    product_id: p.id, sku: p.sku, name: p.name, manufacturer_code: p.manufacturer_code,
    supplier_name: p.supplier_name, unit: p.unit_of_measure || "pz", price: p.price || 0, qty: p.stock ?? 0,
  })), [products]);
  const addPlanned = (pid) => {
    const p = productOptions.find((x) => x.product_id === pid);
    if (!p || (form.planned_materials || []).some((m) => m.product_id === pid)) { setSelProd(""); return; }
    setForm((f) => ({ ...f, planned_materials: [...(f.planned_materials || []), { product_id: p.product_id, sku: p.sku, name: p.name, description: p.name, manufacturer_code: p.manufacturer_code || "", supplier_name: p.supplier_name || "", unit: p.unit, qty: 1, price: p.price }] }));
    setSelProd("");
  };
  const setPlannedQty = (idx, qty) => setForm((f) => ({ ...f, planned_materials: f.planned_materials.map((m, i) => (i === idx ? { ...m, qty: parseFloat(qty) || 1 } : m)) }));
  const delPlanned = (idx) => setForm((f) => ({ ...f, planned_materials: f.planned_materials.filter((_, i) => i !== idx) }));

  const catNames = categories.map((c) => c.name);
  const catColor = (name) => categories.find((c) => c.name === name)?.color || CAT_COLOR[name] || "#64748b";
  const enumOptions = (id) => {
    if (id === "committente") return committenti.map((c) => ({ v: c.name, l: c.name }));
    if (id === "assigned_to_name") return tecnici.map((t) => ({ v: t.name, l: t.name }));
    if (id === "category") return catNames.map((c) => ({ v: c, l: c }));
    if (id === "status") return statusOptions.map((s) => ({ v: s.v, l: s.l }));
    if (id === "priority") return PRIORITY.map((p) => ({ v: p.v, l: p.l }));
    if (id === "picco") return PICCO_OPTIONS.map((p) => ({ v: p, l: p }));
    if (id === "amministrazione") return [{ v: "Contabilizzato", l: "Contabilizzato" }, { v: "Pagato", l: "Pagato" }, { v: "Da contabilizzare", l: "Da contabilizzare" }];
    return [];
  };

  const colW = (c) => colState.widths?.[c.id] ?? (isMnt && c.id === "code" ? 230 : c.w);
  const cols = colState.order.map((id) => ALL_COLS.find((c) => c.id === id)).filter((c) => c && !colState.hidden.includes(c.id));
  const totalWidth = cols.reduce((s, c) => s + colW(c), 0) + (canManage ? ACTIONS_W : 0);
  const startResize = (e, id) => {
    e.preventDefault(); e.stopPropagation();
    const startX = e.clientX;
    const startW = colState.widths?.[id] ?? (ALL_COLS.find((c) => c.id === id)?.w || 120);
    const onMove = (ev) => setColState((s) => ({ ...s, widths: { ...s.widths, [id]: Math.max(60, startW + (ev.clientX - startX)) } }));
    const onUp = () => { document.removeEventListener("mousemove", onMove); document.removeEventListener("mouseup", onUp); };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const isDup = (t, field) => (field === "id_olo" ? t.dup_olo : field === "id_risorsa" ? t.dup_risorsa : field === "client_name" ? t.dup_client : false);

  const toggleSort = (id) => setSort((s) => (s.col === id ? { col: id, dir: s.dir === "asc" ? "desc" : "asc" } : { col: id, dir: "asc" }));
  const filterActive = (id) => (ENUM_COLS.includes(id) || DATE_FILTER_COLS.includes(id)) ? (filters[id]?.length > 0) : !!filters[id];
  const toggleEnumVal = (id, v) => setFilters((f) => {
    const cur = f[id] || [];
    return { ...f, [id]: cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v] };
  });

  const onDrop = (targetId) => {
    if (!dragCol || dragCol === targetId) return;
    setColState((s) => {
      const order = [...s.order];
      order.splice(order.indexOf(targetId), 0, order.splice(order.indexOf(dragCol), 1)[0]);
      return { ...s, order };
    });
    setDragCol(null);
  };

  const create = async (skipDup = false) => {
    if (!skipDup && (form.id_olo || form.id_risorsa || form.client_name)) {
      try {
        const { data: dups } = await api.get("/tickets/duplicates", { params: { id_olo: form.id_olo || "", id_risorsa: form.id_risorsa || "", client_name: form.client_name || "" } });
        if (dups.length) { setDupList(dups); setDupOpen(true); return; }
      } catch (e) { /* prosegui senza bloccare */ }
    }
    setSaving(true);
    try {
      const p = { ...form, duration_min: parseInt(form.duration_min) || 120, area };
      ["committente_id", "assigned_to", "scheduled_date", "appointment_time", "start_time"].forEach((k) => { if (!p[k]) delete p[k]; });
      const { data: created } = await api.post("/tickets", p);
      toast.success("Ticket creato"); setOpen(false); setDupOpen(false); setForm(empty); fetchFirst(true);
      if (skipDup && created?.id) navigate(`/tickets/${created.id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const remove = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Eliminare il ticket?")) return;
    try {
      await api.delete(`/tickets/${id}`);
      toast.success("Ticket eliminato");
      setItems((prev) => (prev || []).filter((x) => x.id !== id));
      setTotal((n) => Math.max(0, n - 1));
    } catch (err) { toast.error(apiError(err)); }
  };
  const duplicate = async (e, t) => {
    e.stopPropagation();
    if (!window.confirm(`Duplicare il ticket ${t.code}? Verrà creato un nuovo ticket con le stesse informazioni.`)) return;
    try {
      const { data: created } = await api.post(`/tickets/${t.id}/duplicate`);
      toast.success(`Ticket duplicato: ${created.code}`);
      navigate(`/tickets/${created.id}`, { state: { openEditOnLoad: true } });
    } catch (err) { toast.error(apiError(err)); }
  };

  const reassign = async (t, techId) => {
    try {
      const { data } = await api.put(`/tickets/${t.id}`, { assigned_to: techId === "none" ? "" : techId });
      setItems((prev) => (prev || []).map((x) => (x.id === t.id ? { ...x, assigned_to: data.assigned_to, assigned_to_name: data.assigned_to_name } : x)));
      toast.success(techId === "none" ? "Assegnazione rimossa" : `Assegnato a ${data.assigned_to_name}`);
    } catch (err) { toast.error(apiError(err)); }
  };

  const exportCSV = async () => {
    setExporting(true);
    try {
      let all = []; let p = 1; let more = true;
      while (more && p <= 500) {
        const { data } = await api.get("/tickets", { params: { ...buildParams(p), page_size: 300 } });
        all = all.concat(data.items); more = data.has_more; p += 1;
      }
      const hasStato = cols.some((c) => c.id === "status");
      const hasPicco = cols.some((c) => c.id === "picco");
      const extraHeaders = ["Attività eseguite", ...(hasStato ? [] : ["Stato"]), ...(hasPicco ? [] : ["Picco"])];
      const headers = [...cols.map((c) => c.label), ...extraHeaders];
      const data = all.map((t) => [
        ...cols.map((c) => String(textVal(t, c.id) || "")),
        (t.performed_activities || []).join(", "),
        ...(hasStato ? [] : [STATUS_LABEL[t.status] || t.status || ""]),
        ...(hasPicco ? [] : [t.picco || ""]),
      ]);
      const res = await api.post("/tickets/export-xlsx", { headers, rows: data }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `ticket_${toISO(new Date())}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Esportati ${all.length} ticket`);
    } catch { toast.error("Errore durante l'esportazione"); } finally { setExporting(false); }
  };

  const renderCell = (t, c) => {
    if (c.id === "assigned_to_name") {
      if (!canManage) return <span className="truncate whitespace-nowrap text-navy">{t.assigned_to_name || "—"}</span>;
      return (
        <div onClick={(e) => e.stopPropagation()} className="w-full">
          <Select value={t.assigned_to || "none"} onValueChange={(v) => reassign(t, v)}>
            <SelectTrigger className="h-7 w-full text-xs" data-testid={`assign-${t.code}`}><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Non assegnato</SelectItem>
              {tecnici.map((tc) => <SelectItem key={tc.id} value={tc.id}>{tc.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      );
    }
    switch (c.kind) {
      case "code": return (
        <span className="inline-flex items-center gap-1 truncate font-mono font-semibold text-primary">
          {t.code}{t.overdue && <span className="text-red-500">•</span>}
          {isMnt && t.source_ticket_code && (
            <span className="inline-flex items-center gap-0.5 whitespace-nowrap rounded-full border border-purple-200 bg-purple-100 px-1.5 py-0.5 text-[10px] font-bold text-purple-800" title={`Arrivato da Ticket - Delivery & Assurance (${t.source_ticket_code})`} data-testid={`from-delivery-${t.code}`}>
              Da Delivery · {t.source_ticket_code}
            </span>
          )}
          {t.anticipo && (
            <span className={cn("inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[9px] font-bold uppercase", t.anticipo === "anticipato" ? "bg-amber-100 text-amber-800" : "bg-sky-100 text-sky-800")} title={`Intervento ${t.anticipo}`} data-testid={`code-anticipo-${t.code}`}>
              <AlertTriangle size={10} className="shrink-0" />{t.anticipo === "anticipato" ? "ANTIC." : "POST."}
            </span>
          )}
        </span>
      );
      case "cat": return <span className="inline-flex items-center gap-2 truncate whitespace-nowrap text-navy"><span className="h-2 w-2 shrink-0 rounded-full" style={{ background: catColor(t.category) }} />{t.category}</span>;
      case "status": return <StatoBadge stato={t.status} />;
      case "priority": return <PrioritaBadge priorita={t.priority} />;
      case "date": return (
        <span className={cn("inline-flex items-center gap-1 truncate whitespace-nowrap", t.anticipo ? "rounded-md border border-amber-300 bg-amber-50 px-1.5 py-0.5 font-semibold text-amber-800" : "text-navy")} title={t.anticipo ? `Intervento ${t.anticipo}` : ""}>
          {t.anticipo && <AlertTriangle size={12} className="shrink-0 text-amber-600" />}{appuntamento(t) || "—"}
        </span>
      );
      case "interval": return <span className="truncate whitespace-nowrap text-navy">{intervento(t) || "—"}</span>;
      case "created": return <span className="truncate whitespace-nowrap text-muted-foreground">{fmtDate(t.created_at) || "—"}</span>;
      case "aging": return <span className={cn("font-semibold", t.aging_days > 7 ? "text-red-600" : "text-navy")}>{t.aging_days}g</span>;
      case "admin": return (
        <div className="flex gap-1">
          {t.paid ? <span className="rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-bold text-blue-700">Pagato</span>
            : t.billed ? <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">Contabilizzato</span>
            : <span className="text-xs text-muted-foreground">Da contabilizzare</span>}
        </div>
      );
      default: {
        const v = t[c.id];
        if (["id_olo", "id_risorsa", "client_name"].includes(c.id) && isDup(t, c.id)) {
          return (
            <span className="inline-flex items-center gap-1 truncate whitespace-nowrap rounded-md border border-amber-300 bg-amber-50 px-1.5 py-0.5 font-semibold text-amber-800" data-testid={`dup-cell-${c.id}-${t.code}`} title="Valore presente anche in altri ticket">
              <AlertTriangle size={12} className="shrink-0 text-amber-600" />{v || "—"}
            </span>
          );
        }
        return <span className="truncate whitespace-nowrap text-navy">{v || "—"}</span>;
      }
    }
  };

  const virtualRows = rowVirtualizer.getVirtualItems();

  return (
    <div className="flex h-[calc(100vh-3rem)] min-h-0 flex-col gap-4 animate-fade-up">
      <PageHeader overline="Flux · Gestione Ticket" title={isMnt ? "Ticket - Manutenzione" : "Ticket - Delivery & Assurance"} subtitle={isMnt ? "Copie dei ticket inviate per lavorazioni approfondite — completandole il ticket Delivery passa in “Da Sbloccare”" : "Elenco interventi — scorrimento infinito virtualizzato, filtri e ordinamento lato server"}>
        <QuickEnumFilter label="Categoria" colId="category" options={enumOptions("category")} selected={filters.category} setFilters={setFilters} />
        <QuickEnumFilter label="Stato" colId="status" options={enumOptions("status")} selected={filters.status} setFilters={setFilters} />
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" data-testid="ticket-date-filter">
              <CalendarDays size={16} />
              {dateFilters.length === 0 ? "Tutte le date" : dateFilters.length === 1 ? toISO(dateFilters[0]) : `${dateFilters.length} giorni`}
              {dateFilters.length > 0 && (
                <span onClick={(e) => { e.stopPropagation(); setDateFilters([]); }} data-testid="ticket-date-filter-clear" className="ml-1 rounded-full p-0.5 hover:bg-secondary"><X size={13} /></span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar mode="multiple" selected={dateFilters} onSelect={(d) => setDateFilters(d || [])} />
            <div className="flex justify-between border-t border-border p-2">
              <Button variant="ghost" size="sm" onClick={() => setDateFilters([])}>Azzera</Button>
              <span className="self-center text-xs text-muted-foreground">{dateFilters.length} selezionati</span>
            </div>
          </PopoverContent>
        </Popover>
        <Button variant="outline" onClick={exportCSV} disabled={exporting} data-testid="ticket-export-btn"><Download size={16} />{exporting ? "Esporto..." : "Esporta"}</Button>
        <Popover>
          <PopoverTrigger asChild><Button variant="outline" data-testid="cols-btn"><Columns3 size={16} />Colonne</Button></PopoverTrigger>
          <PopoverContent align="end" className="w-56">
            <p className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">Colonne visibili</p>
            <div className="max-h-72 space-y-1.5 overflow-y-auto">
              {ALL_COLS.map((c) => (
                <label key={c.id} className="flex cursor-pointer items-center gap-2 text-sm">
                  <Checkbox checked={!colState.hidden.includes(c.id)} onCheckedChange={(v) => setColState((s) => ({ ...s, hidden: v ? s.hidden.filter((h) => h !== c.id) : [...s.hidden, c.id] }))} data-testid={`col-toggle-${c.id}`} />
                  {c.label}
                </label>
              ))}
            </div>
          </PopoverContent>
        </Popover>
        {canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-ticket-btn"><Plus size={18} />Nuovo ticket</Button></DialogTrigger>
            <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
              <DialogHeader><DialogTitle>{isMnt ? "Nuovo ticket di Manutenzione" : "Nuovo ticket"}</DialogTitle></DialogHeader>
              <DialogDescription className="sr-only">Compila i campi del nuovo ticket. Nessun campo è obbligatorio.</DialogDescription>
              <div className="grid gap-4 py-2">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5"><Label>Cliente</Label><Input data-testid="ticket-cliente" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Telefono</Label><Input value={form.client_phone} onChange={(e) => setForm({ ...form, client_phone: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5"><Label>Committente</Label>
                    <Select value={form.committente_id || "none"} onValueChange={(v) => setForm({ ...form, committente_id: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid="ticket-committente"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent><SelectItem value="none">—</SelectItem>{committenti.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5"><Label>Categoria</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger data-testid="ticket-categoria"><SelectValue /></SelectTrigger>
                      <SelectContent>{catNames.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5"><Label>Priorità</Label>
                    <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                      <SelectTrigger data-testid="ticket-priorita"><SelectValue /></SelectTrigger>
                      <SelectContent>{PRIORITY.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5"><Label>Tecnico</Label>
                    <Select value={form.assigned_to || "none"} onValueChange={(v) => setForm({ ...form, assigned_to: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid="ticket-tecnico"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent><SelectItem value="none">Non assegnato</SelectItem>{tecnici.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5"><Label>Indirizzo</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Comune</Label><Input value={form.comune} onChange={(e) => setForm({ ...form, comune: e.target.value })} /></div>
                </div>
                <div className="rounded-lg border border-border bg-secondary/30 p-3">
                  <p className="mb-2 text-xs font-bold uppercase tracking-wider text-primary">Appuntamento cliente</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5"><Label>Data</Label><DateField testid="ticket-data" value={form.scheduled_date} onChange={(v) => setForm((f) => ({ ...f, scheduled_date: v, intervention_date: v }))} /></div>
                    <div className="space-y-1.5"><Label>Orario appuntamento</Label><TimeField testid="ticket-appointment" value={form.appointment_time} onChange={(v) => setForm((f) => ({ ...f, appointment_time: v, start_time: v }))} /></div>
                  </div>
                </div>
                <div className="rounded-lg border border-border bg-secondary/30 p-3">
                  <p className="mb-2 text-xs font-bold uppercase tracking-wider text-primary">Intervento previsto</p>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-1.5"><Label>Data intervento</Label><DateField testid="ticket-int-date" value={form.intervention_date} onChange={(v) => setForm((f) => ({ ...f, intervention_date: v, scheduled_date: f.scheduled_date || v }))} /></div>
                    <div className="space-y-1.5"><Label>Ora inizio</Label><TimeField testid="ticket-start" value={form.start_time} onChange={(v) => setForm((f) => ({ ...f, start_time: v }))} /></div>
                    <div className="space-y-1.5"><Label>Durata (min)</Label><Input type="number" data-testid="ticket-duration" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} /></div>
                  </div>
                  {form.intervention_date && form.scheduled_date && form.intervention_date !== form.scheduled_date && (
                    <p className="mt-2 text-xs font-semibold text-amber-700">Attenzione: data intervento diversa dall'appuntamento ({form.intervention_date < form.scheduled_date ? "anticipato" : "posticipato"}).</p>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-1.5"><Label>ID OLO</Label><Input data-testid="ticket-idolo" value={form.id_olo} onChange={(e) => setForm({ ...form, id_olo: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>ID Risorsa</Label><Input value={form.id_risorsa} onChange={(e) => setForm({ ...form, id_risorsa: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>PFS</Label><Input data-testid="ticket-pfs" value={form.pfs} onChange={(e) => setForm({ ...form, pfs: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5"><Label>Password apparati</Label><Input data-testid="ticket-device-pwd" value={form.device_password} onChange={(e) => setForm({ ...form, device_password: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>ID Servizi</Label><Input data-testid="ticket-id-servizi" value={form.id_servizi} onChange={(e) => setForm({ ...form, id_servizi: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5"><Label>Picco</Label>
                    <Select value={form.picco || "none"} onValueChange={(v) => setForm({ ...form, picco: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid="ticket-picco"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent><SelectItem value="none">—</SelectItem>{PICCO_OPTIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="rounded-lg border border-border bg-secondary/30 p-3">
                  <p className="mb-2 text-xs font-bold uppercase tracking-wider text-primary">Materiali da installare</p>
                  {(form.planned_materials || []).length > 0 && (
                    <div className="mb-3 space-y-2">
                      {(form.planned_materials || []).map((m, idx) => (
                        <div key={idx} className="flex items-center gap-3 rounded-lg border border-border bg-card p-2.5" data-testid={`create-planned-${idx}`}>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-semibold text-navy">{m.name}</p>
                            <p className="font-mono text-xs text-muted-foreground">Cod. produttore: {m.manufacturer_code || "—"} · Fornitore: {m.supplier_name || "—"}</p>
                          </div>
                          <Input type="number" min="1" className="h-8 w-20" value={m.qty} onChange={(e) => setPlannedQty(idx, e.target.value)} data-testid={`create-planned-qty-${idx}`} />
                          <span className="text-xs text-muted-foreground">{m.unit}</span>
                          <button type="button" onClick={() => delPlanned(idx)} className="text-muted-foreground hover:text-destructive" data-testid={`create-planned-del-${idx}`}><Trash2 size={16} /></button>
                        </div>
                      ))}
                    </div>
                  )}
                  <ProductCombobox products={productOptions} value={selProd} onChange={addPlanned} placeholder="Cerca e seleziona un materiale da installare..." testid="create-planned-combo" />
                </div>
                <div className="space-y-1.5"><Label>Descrizione</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              </div>
              <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button><Button onClick={() => create()} disabled={saving} data-testid="ticket-save">Crea ticket</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[240px] flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="ticket-search" className="pl-9" placeholder="Cerca per codice o cliente..." value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        {(Object.values(filters).some((v) => (Array.isArray(v) ? v.length : v))) && (
          <Button variant="ghost" size="sm" onClick={() => setFilters({})} data-testid="clear-filters"><X size={14} />Azzera filtri</Button>
        )}
        <span className="text-sm text-muted-foreground" data-testid="ticket-count">{total != null ? `${total} ticket` : `${items?.length || 0}+ ticket`}</span>
      </div>

      <div ref={parentRef} onScroll={onScroll} className="min-h-0 flex-1 overflow-auto rounded-xl border border-border bg-card">
        <div style={{ minWidth: totalWidth }}>
          <div className="sticky top-0 z-10 flex border-b border-border bg-secondary">
            {cols.map((c) => (
              <div key={c.id} draggable onDragStart={() => setDragCol(c.id)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(c.id)}
                style={{ width: colW(c) }}
                className="group relative flex shrink-0 items-center gap-1 whitespace-nowrap px-3 py-2.5 text-xs font-bold uppercase tracking-wider text-muted-foreground" data-testid={`col-${c.id}`}>
                <GripVertical size={12} className="cursor-move opacity-0 group-hover:opacity-50" />
                <button onClick={() => toggleSort(c.id)} className="flex items-center gap-1 hover:text-navy" data-testid={`sort-${c.id}`}>
                  {c.label}
                  {sort.col === c.id ? (sort.dir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ChevronsUpDown size={11} className="opacity-30" />}
                </button>
                {SERVER_FILTERABLE.includes(c.id) && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <button className={cn("rounded p-0.5 hover:text-navy", filterActive(c.id) ? "text-primary" : "text-muted-foreground/50")} data-testid={`filter-btn-${c.id}`}>
                        <Filter size={12} className={filterActive(c.id) ? "fill-primary" : ""} />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className={cn("p-2", DATE_FILTER_COLS.includes(c.id) ? "w-auto" : "w-52")}>
                      {ENUM_COLS.includes(c.id) ? (
                        <div>
                          <div className="mb-2 flex items-center justify-between gap-2 border-b border-border pb-2">
                            <button onClick={() => setFilters((f) => ({ ...f, [c.id]: enumOptions(c.id).map((o) => o.v) }))} className="text-xs font-semibold text-primary hover:underline" data-testid={`col-select-all-${c.id}`}>Seleziona tutto</button>
                            <button onClick={() => setFilters((f) => ({ ...f, [c.id]: [] }))} className="text-xs font-semibold text-muted-foreground hover:text-navy" data-testid={`col-deselect-all-${c.id}`}>Deseleziona tutto</button>
                          </div>
                          <div className="max-h-64 space-y-1 overflow-y-auto">
                            {enumOptions(c.id).map((o) => (
                              <label key={o.v} className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm hover:bg-secondary">
                                <Checkbox checked={(filters[c.id] || []).includes(o.v)} onCheckedChange={() => toggleEnumVal(c.id, o.v)} data-testid={`filter-${c.id}-${o.v}`} />
                                {o.l}
                              </label>
                            ))}
                          </div>
                        </div>
                      ) : DATE_FILTER_COLS.includes(c.id) ? (
                        <div>
                          <div className="mb-1 flex items-center justify-between gap-2 border-b border-border px-1 pb-1">
                            <span className="text-xs font-semibold text-muted-foreground">{(filters[c.id] || []).length === 1 ? "1 data" : `${(filters[c.id] || []).length} date`}</span>
                            <button onClick={() => setFilters((f) => ({ ...f, [c.id]: [] }))} className="text-xs font-semibold text-primary hover:underline" data-testid={`filter-${c.id}-clear`}>Azzera</button>
                          </div>
                          <Calendar mode="multiple" selected={(filters[c.id] || []).map((s) => new Date(`${s}T00:00:00`))} onSelect={(d) => setFilters((f) => ({ ...f, [c.id]: (d || []).map(toISO) }))} data-testid={`filter-${c.id}-cal`} />
                        </div>
                      ) : (
                        <Input autoFocus placeholder={`Filtra ${c.label}`} value={filters[c.id] || ""} onChange={(e) => setFilters((f) => ({ ...f, [c.id]: e.target.value }))} data-testid={`filter-${c.id}`} />
                      )}
                    </PopoverContent>
                  </Popover>
                )}
                <div onMouseDown={(e) => startResize(e, c.id)} onClick={(e) => e.stopPropagation()} draggable={false} onDragStart={(e) => e.preventDefault()} title="Trascina per ridimensionare" data-testid={`col-resize-${c.id}`} className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize bg-transparent hover:bg-primary/40" />
              </div>
            ))}
            {canManage && <div style={{ width: ACTIONS_W }} className="shrink-0" />}
          </div>

          {items === null ? (
            <div className="px-4 py-12 text-center text-muted-foreground">Caricamento...</div>
          ) : items.length === 0 ? (
            <div className="px-4 py-12 text-center text-muted-foreground">Nessun ticket trovato</div>
          ) : (
            <div style={{ height: rowVirtualizer.getTotalSize(), position: "relative" }}>
              {virtualRows.map((vi) => {
                const t = items[vi.index];
                if (!t) return null;
                return (
                  <div key={t.id} data-testid={`ticket-row-${t.code}`} onClick={() => navigate(`/tickets/${t.id}`, { state: { from: isMnt ? "/manutenzione" : "/tickets" } })}
                    className="absolute left-0 top-0 flex w-full cursor-pointer border-b border-border/60 hover:bg-secondary/40"
                    style={{ height: vi.size, transform: `translateY(${vi.start}px)` }}>
                    {cols.map((c) => (
                      <div key={c.id} style={{ width: colW(c) }} className="flex shrink-0 items-center overflow-hidden px-3 text-sm">
                        {renderCell(t, c)}
                      </div>
                    ))}
                    {canManage && (
                      <div style={{ width: ACTIONS_W }} className="flex shrink-0 items-center justify-end gap-2 px-3">
                        <button onClick={(e) => duplicate(e, t)} data-testid={`duplicate-ticket-${t.code}`} title="Duplica ticket" className="text-muted-foreground hover:text-primary"><Copy size={16} /></button>
                        <button onClick={(e) => remove(e, t.id)} data-testid={`delete-ticket-${t.code}`} title="Elimina ticket" className="text-muted-foreground hover:text-destructive"><Trash2 size={16} /></button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {loading && items !== null && items.length > 0 && (
            <div className="py-3 text-center text-xs text-muted-foreground" data-testid="loading-more">Caricamento…</div>
          )}
        </div>
      </div>

      <Dialog open={dupOpen} onOpenChange={setDupOpen}>
        <DialogContent className="max-w-lg" data-testid="dup-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle size={18} className="text-amber-600" />Possibili ticket duplicati</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-1">
            <p className="text-sm text-muted-foreground">Esistono già ticket con gli stessi dati. Verifica prima di procedere:</p>
            <div className="max-h-72 space-y-2 overflow-y-auto">
              {dupList.map((d) => (
                <button key={d.id} onClick={() => navigate(`/tickets/${d.id}`)} data-testid={`dup-item-${d.code}`}
                  className="flex w-full items-center justify-between gap-3 rounded-lg border border-border bg-card p-3 text-left transition-colors hover:border-primary hover:bg-accent">
                  <div className="min-w-0">
                    <p className="font-mono text-xs font-bold text-primary">{d.code} · {STATUS_LABEL[d.status] || d.status}</p>
                    <p className="truncate text-sm font-semibold text-navy">{d.client_name || "—"}</p>
                    <p className="truncate text-xs text-muted-foreground">{[d.id_olo && `OLO ${d.id_olo}`, d.id_risorsa && `Ris. ${d.id_risorsa}`].filter(Boolean).join(" · ")}</p>
                  </div>
                  <span className="shrink-0 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-700">{d.matched.join(", ")}</span>
                </button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDupOpen(false)} data-testid="dup-cancel">Annulla</Button>
            <Button onClick={() => create(true)} disabled={saving} data-testid="dup-create-anyway">Crea comunque</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
